import { useSelector } from "react-redux";
import {
  PERMISSIONS,
  ROLES,
  hasRole,
  getHighestRole,
  canAccessFeature,
} from "../utils/RolePermissionsMapping";

const usePermissions = () => {
  const authUserData = useSelector((state) => state.auth.authUserData);
  const userRoles = authUserData?.prestador?.roles || [];
  const userPermissions = authUserData?.prestador?.permisos || [];

  const hasPermission = (permission) => {
    return userPermissions.includes(permission);
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some((permission) =>
      userPermissions.includes(permission)
    );
  };

  const hasAllPermissions = (permissions) => {
    return permissions.every((permission) =>
      userPermissions.includes(permission)
    );
  };

  const hasUserRole = (role) => {
    return hasRole(userRoles, role);
  };

  const canAccess = (requiredPermissions) => {
    return canAccessFeature(userRoles, requiredPermissions);
  };

  // Roles específicos
  const isMaster = hasUserRole(ROLES.MASTER);
  const isAdmin = hasUserRole(ROLES.ADMIN) || hasUserRole(ROLES.ADMINISTRADOR);
  const isMonitor = hasUserRole(ROLES.MONITOR);
  const highestRole = getHighestRole(userRoles);

  return {
    permissions: userPermissions,
    roles: userRoles,
    highestRole,
    isMaster,
    isAdmin,
    isMonitor,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasUserRole,
    canAccess,

    // Specific permission checks for Stand Virtual modules based on actual permissions
    canAccessStandVirtualConfig: hasPermission(
      PERMISSIONS.CONFIG_STAND_VIRTUAL
    ),
    canViewStandVirtualConfig: hasPermission(
      PERMISSIONS.CONFIG_STAND_VIRTUAL_VIEW
    ),
    canEditStandVirtualConfig: hasPermission(
      PERMISSIONS.CONFIG_STAND_VIRTUAL_EDIT
    ),

    canAccessDocuments: hasPermission(PERMISSIONS.CONFIG_DOCUMENTS),
    canViewDocuments: hasPermission(PERMISSIONS.CONFIG_DOCUMENTS_VIEW),
    canEditDocuments: hasPermission(PERMISSIONS.CONFIG_DOCUMENTS_EDIT),

    canViewReports: hasPermission(PERMISSIONS.REPORT_VIEW),
    canExportReports: hasPermission(PERMISSIONS.REPORT_EXPORT),
    canExportExcel: hasPermission(PERMISSIONS.REPORT_EXCEL),
    canExportPDF: hasPermission(PERMISSIONS.REPORT_PDF),
    canViewMetrics: hasPermission(PERMISSIONS.METRICS_VIEW),

    canManageUsers: hasPermission(PERMISSIONS.ADMIN_MANAGE_USERS),
    canViewUsers: hasPermission(PERMISSIONS.USER_VIEW),
    canSearchUsers: hasPermission(PERMISSIONS.USER_SEARCH),
    canCreateUsers: hasPermission(PERMISSIONS.USER_CREATE),
    canUpdateUsers: hasPermission(PERMISSIONS.USER_UPDATE),
    canDeleteUsers: hasPermission(PERMISSIONS.USER_DELETE),
    canAssignRoles: hasPermission(PERMISSIONS.ROLE_ASSIGN),

    canEnablePSS: hasPermission(PERMISSIONS.CONFIG_PSS_ENABLE),
    canConfigureProcedures: hasPermission(PERMISSIONS.CONFIG_PROCEDURES),
    canConfigureCoverages: hasPermission(PERMISSIONS.CONFIG_COVERAGES),
    canConfigurePSSTypes: hasPermission(PERMISSIONS.CONFIG_PSS_TYPES),
    canConfigureActivations: hasPermission(PERMISSIONS.CONFIG_ACTIVATIONS),
  };
};

export default usePermissions;
